import {
  IQuoteSettingsConfigSchema,
  QuoteSettingsDefaultAddressSourceEnum,
  SigningOrderEnum,
} from '@monetize/types/app';
import React, { useEffect } from 'react';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  ExpandableAccordion,
  MBox,
  MPageContainer,
  MSettingsPageHeader,
  MSkeleton,
  MVStack,
} from '../../../../components/Monetize';
import { ExpandCollapseButton } from '../../../../components/Monetize/ExpandCollapseButton';
import { SETTINGS_CONTENT_MAX_WIDTH } from '../../../../constants/settings';
import { useExpandAllControl, useQuoteSettings } from '../../../../hooks';
import { useDocumentHead } from '../../../../services/documentHead';
import { useFlags } from '../../../../services/launchDarkly';
import { QUOTE_SETTINGS_RENDERING_REGISTRY } from './quoteSettings.registry';
import { QUOTE_SETTINGS_CONFIG } from './quoteSettingsConfig';

const QuoteSettings = () => {
  const { setDocTitle } = useDocumentHead();
  const { fetchLoading, quoteSettings, doSaveQuoteSettings } =
    useQuoteSettings();

  useEffect(() => {
    setDocTitle('Settings', 'Quote Settings');
  }, []);

  const {
    isAllExpanded,
    commandExpanded,
    expandCommandSeq,
    toggleExpandAll,
    onChildExpandChange,
  } = useExpandAllControl();

  const flags = useFlags();

  const handleSave = async (
    payload: IQuoteSettingsConfigSchema,
  ): Promise<void> => {
    try {
      await doSaveQuoteSettings({
        ...payload,
        defaultAddressSource:
          payload.defaultAddressSource ||
          QuoteSettingsDefaultAddressSourceEnum.CONTACT,
        signingOrder: payload.signingOrder || SigningOrderEnum.EXTERNAL_FIRST,
      });
    } catch (ex) {
      handleApiErrorToast(ex);
      throw ex;
    }
  };

  return (
    <MPageContainer data-testid="quote-settings-list">
      <MSettingsPageHeader title="Quote Settings" />

      <MBox w={'100%'} mt={0} overflow="auto" mb="6" p={1}>
        {fetchLoading && (
          <MVStack maxW={SETTINGS_CONTENT_MAX_WIDTH} gap={4}>
            <MSkeleton height={50} width="100%" />
            <MSkeleton height={50} width="100%" />
            <MSkeleton height={50} width="100%" />
          </MVStack>
        )}
        {!fetchLoading && quoteSettings && (
          <MVStack
            maxW={SETTINGS_CONTENT_MAX_WIDTH}
            gap={4}
            alignItems="flex-start"
          >
            <ExpandCollapseButton
              isExpanded={isAllExpanded}
              collapsedLabel="Expand all"
              expandedLabel="Collapse all"
              onChange={toggleExpandAll}
            />

            <ExpandableAccordion
              isAllExpanded={commandExpanded}
              expandCommandSeq={expandCommandSeq}
              onExpandChange={onChildExpandChange}
            >
              {QUOTE_SETTINGS_RENDERING_REGISTRY.filter(({ sectionKey }) => {
                const def = QUOTE_SETTINGS_CONFIG.find(
                  (e) => e.key === sectionKey,
                );
                return !def?.featureFlag || flags[def.featureFlag];
              }).map(({ sectionKey, render }) => (
                <React.Fragment key={sectionKey}>
                  {render({
                    quoteSettings,
                    isLoading: fetchLoading,
                    handleSave,
                  })}
                </React.Fragment>
              ))}
            </ExpandableAccordion>
          </MVStack>
        )}
      </MBox>
    </MPageContainer>
  );
};
export default QuoteSettings;
